
import { jsonrepair } from "jsonrepair";
import { Component, Vue, Watch } from "vue-property-decorator";
import {
  cleanJson,
  cleanJsonBeforeParse,
  fetchAiSettings,
  getEventSource,
  getTextFromChunk,
  persistMessages,
  requestCompletion,
  saveAiSettings,
  talkToUnsplash
} from "@/lib/ai/api";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import {
  getElementDescription,
  getElementFriendlyName,
  getElementQualifier,
  getElementTemperature,
  getFieldsToComplete,
  getImagesToComplete,
  getLockedFieldsPath,
  handleAiError,
  processCompletion,
  saveToElementMetadata,
  updateDocument,
  updateImages
} from "@/lib/ai/ai";
import { formatFieldsForOpenAI, getPrompt } from "@/lib/ai/prompts";
import {
  configStore,
  documentStore,
  globalStore,
  uiStore
} from "@/store/store";
import {
  ChatMessage,
  FieldToComplete,
  PromptInfo,
  PromptSettings
} from "@/lib/ai/types";
import AIUnsplash from "./AIUnsplash.vue";
import AISettings from "./AISettings.vue";
import AIWriter from "./AIWriter.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import Icon from "@/components/UI/Icon.vue";
import NotificationLogger from "@/lib/notifications/notifications";
import Tab from "@/components/Reusable/Tabs/Tab.vue";
import TabsRow from "@/components/Reusable/Tabs/TabsRow.vue";
import { closeUiPanel } from "@/lib/panels/panels";
import PanelHeader from "@/components/Reusable/Util/PanelHeader.vue";
import AIPluginNotInstalled from "./AIPluginNotInstalled.vue";
import AINoApiKey from "./AINoApiKey.vue";
import AINoElementSelected from "./AINoElementSelected.vue";
import { ClosingConfirmationChosenOption } from "@/types/ui";
import defensiveCopy from "@/util/defensive-copy";

@Component({
  components: {
    Tab,
    Icon,
    TabsRow,
    NiceButton,
    PanelHeader,
    AIWriter,
    AIUnsplash,
    AISettings,
    AINoApiKey,
    AIPluginNotInstalled,
    AINoElementSelected
  }
})
export default class AIPanel extends Vue {
  rewriteInProgress = false;
  savingSettings = false;
  loadingSettings = false;
  pluginNotInstalled = false;

  activeTab = "content";
  tab: "write" | "images" | "settings" = "write";

  maxRetries = 3;
  retries = 0;

  get element() {
    return uiStore.activeElement || null;
  }

  get elementConfig() {
    return this.element ? configStore.element(this.element.data.type) : null;
  }

  get isAIDisabled() {
    return (
      this.elementConfig?.settings && this.elementConfig?.settings.disableAI
    );
  }

  get settings() {
    return globalStore.aiSettings;
  }

  get prompt(): PromptInfo {
    return {
      sectionDescription: this.elementDescription,
      qualifiers: this.qualifiers,
      temperature: this.temperature,
      lockedFields: this.lockedFieldsPaths,
      locale: this.settings.locale,
      unsplash: {
        enabled: false,
        fillEmptyImagesOnly: false,
        keywords: ""
      },
      singularity: {
        sectionData: {
          type: false,
          index: false
        },
        shouldGenerateOnInitialLoad: false,
        unsplashKeywords: []
      },
      onboarding: {
        sectionType: false,
        shouldGenerateOnInitialLoad: false
      }
    };
  }

  get friendlyName() {
    if (!this.element) return "Section";
    return getElementFriendlyName(this.element);
  }

  get elementDescription() {
    if (!this.element) return "";
    return getElementDescription(this.element);
  }

  get qualifiers() {
    if (!this.element) return "";
    return getElementQualifier(this.element);
  }

  get temperature() {
    if (!this.element) return 1;
    return getElementTemperature(this.element);
  }

  get canViewTabs() {
    return this.settings.isApiKeySet && !this.pluginNotInstalled;
  }

  get unsavedChangesPresent() {
    return globalStore.aiSettingsHasUnsavedChangesPresent;
  }

  get lockedFieldsPaths() {
    if (!this.element) return [];
    return getLockedFieldsPath(this.element);
  }

  get lockedFields() {
    if (!this.element) return [];
    return getFieldsToComplete(this.element).filter(node => node.field.locked);
  }

  async fetchAndUpdateImages(keywords: string[]) {
    if (!this.element) return;

    const fields = getImagesToComplete(this.element);
    const { enabled, fillEmptyImagesOnly } = this.prompt.unsplash;

    if (!enabled) return;

    if (!keywords) {
      this.log("Failed to get Unsplash keywords from OpenAI API.");
      return;
    }

    const response = await talkToUnsplash(keywords, fields.length);
    updateImages(fields, response, fillEmptyImagesOnly);
  }

  async syncRewrite(prompt: ChatMessage[], fields: FieldToComplete[]) {
    const completion = pipe(
      await requestCompletion(prompt, "json", "breakdance_ai_request_completion", false),
      fold(
        () => null,
        response => response
      )
    );

    if (!completion) {
      throw new Error("No response was returned from OpenAI API.");
    }

    const parsedCompletion = processCompletion(completion, fields);

    if (!parsedCompletion.elements.length) {
      throw new Error("No elements were returned from OpenAI API.");
    }

    updateDocument(parsedCompletion.elements);
    await this.fetchAndUpdateImages(parsedCompletion.unsplash_keywords);
  }

  async streamRewrite(prompt: ChatMessage[], fields: FieldToComplete[]) {
    // Persist messages to session storage before initiating the stream.
    await persistMessages(prompt, "json", this.temperature);

    const eventSource = getEventSource();
    let parts = "";

    return new Promise((resolve, reject) => {
      eventSource.onmessage = async event => {
        if (event.data === "[DONE]") {
          eventSource.close();

          try {
            const repaired = jsonrepair(parts);
            const parsedCompletion = processCompletion(repaired, fields);

            updateDocument(parsedCompletion.elements);
            console.log("%cOutput", "color: green;");
            console.log(repaired);
            resolve(parts);
          } catch (error) {
            reject(error);
          }
        } else {
          parts += getTextFromChunk(event.data);
          parts = cleanJson(parts);

          if (!parts) return;

          try {
            const cleanJson = cleanJsonBeforeParse(parts);
            const repaired = jsonrepair(cleanJson);
            const parsedCompletion = processCompletion(repaired, fields);

            updateDocument(parsedCompletion.elements);
          } catch (error) {
            // Parsing is allowed to fail here, we'll just try again in the next chunk.
            console.debug(parts);
            console.debug(error);
          }
        }
      };

      eventSource.onerror = event => {
        eventSource.close();
        reject((event as MessageEvent).data);
      };
    });
  }

  async rewrite() {
    if (this.rewriteInProgress) return;
    if (!this.element) return;

    const fields = getFieldsToComplete(this.element);
    const fieldsToFill = formatFieldsForOpenAI(fields);
    const prompt = getPrompt(fieldsToFill, this.prompt, this.settings.prompt);

    this.rewriteInProgress = true;
    saveToElementMetadata(this.element, this.prompt);

    console.log("%cInput", "color: #2196F3;");
    console.log({ fields, prompt });

    if (!fields.length) {
      this.log("This element is empty, please add some content first.");
      this.rewriteInProgress = false;
      return;
    }

    try {
      if (this.settings.isStreamingEnabled) {
        await this.streamRewrite(prompt, fields);
      } else {
        await this.syncRewrite(prompt, fields);
      }
    } catch (error) {
      await this.retry(error);
    }

    this.rewriteInProgress = false;
  }

  async retry(error: unknown) {
    this.rewriteInProgress = false;

    if (this.retries < this.maxRetries) {
      console.log(
        `%cRetrying ${this.retries + 1} of ${this.maxRetries}`,
        "color: #2196F3;"
      );
      this.retries++;
      await this.rewrite();
    } else {
      this.retries = 0;
      console.log("Max retries reached, giving up.");
      this.handleError(error as string);
    }
  }

  handleError(error: string) {
    handleAiError(error);
  }

  updateSettings(newPromptSettings: PromptSettings) {
    const newSettings = defensiveCopy(this.settings);
    newSettings.prompt = newPromptSettings;
    globalStore.setAiSettings(newSettings);
  }

  async savePromptSettings() {
    this.savingSettings = true;
    const successful = await saveAiSettings();
    this.savingSettings = false;
    return successful;
  }

  async loadSettings() {
    this.loadingSettings = true;

    const successful = await fetchAiSettings();

    if (!successful) {
      uiStore.noClosingConfirmationIsNeededFor("ai");
      this.pluginNotInstalled = true;
      this.loadingSettings = false;
      return false;
    }

    this.loadingSettings = false;
    this.pluginNotInstalled = false;

    return true;
  }

  updatePrompt(prompt: PromptInfo) {
    if (!this.element) return;

    documentStore.unthrottledPropertyChanged({
      elementId: this.element.id,
      path: "meta.promptInfo",
      meta: {
        snapshotLabel: `Updated prompt info for element #${this.element.id}`
      },
      value: prompt
    });
  }

  requestPanelClosingConfirmation() {
    uiStore.requestClosingConfirmation({
      subject: "ai",
      confirmationDefinition: {
        title: "You have unsaved changes to your AI Settings.",
        text: "Would you like to save or discard these changes?",
        type: "info"
      }
    });
  }

  @Watch("unsavedChangesPresent", { immediate: true })
  handleUnsavedChangesPresentChange(unsavedChangesPresent: boolean) {
    if (unsavedChangesPresent) {
      this.requestPanelClosingConfirmation();
    } else {
      uiStore.noClosingConfirmationIsNeededFor("ai");
    }
  }

  get confirmationChosenOption(): ClosingConfirmationChosenOption | undefined {
    return uiStore.closingConfirmations.ai?.chosenOption;
  }

  @Watch("confirmationChosenOption", { immediate: true })
  async handleConfirmationChosenOptionChange(
    chosenOption: ClosingConfirmationChosenOption | undefined
  ) {
    if (chosenOption === "discard") {
      uiStore.setClosingConfirmationIsDiscarding({
        subject: "ai",
        isDiscarding: true
      });

      if (await this.loadSettings()) {
        this.close();
      } else {
        this.requestPanelClosingConfirmation();
      }
    } else if (chosenOption === "save") {
      uiStore.setClosingConfirmationIsSaving({
        subject: "ai",
        isSaving: true
      });

      if (await this.savePromptSettings()) {
        this.close();
      } else {
        this.requestPanelClosingConfirmation();
      }
    }
  }

  close() {
    closeUiPanel("ai");
  }

  log(message: string) {
    NotificationLogger.log.errorMessage(message);
  }

  async beforeMount() {
    if (uiStore.panelState.shouldLoadFreshDataIntoStoreOnceOpened.ai) {
      await this.loadSettings();
    } else {
      uiStore.setShouldPanelLoadFreshDataIntoStoreOnceOpened("ai", true);
    }
  }
}
